import React, { useState } from 'react';
import { Plane, Bus, Hotel, Search, Calendar, Users, MapPin, Star, Tag, ChevronRight, Clock, Percent } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const flightDeals = [
  { from: 'Johannesburg', to: 'Cape Town', price: 899, airline: 'FlySafair', duration: '2h 05m', discount: 30, image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=400&h=250&fit=crop' },
  { from: 'Durban', to: 'Johannesburg', price: 749, airline: 'Mango', duration: '1h 15m', discount: 25, image: 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=400&h=250&fit=crop' },
  { from: 'Cape Town', to: 'Durban', price: 1199, airline: 'SAA', duration: '2h 20m', discount: 15, image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop' },
  { from: 'Johannesburg', to: 'Port Elizabeth', price: 1049, airline: 'FlySafair', duration: '1h 45m', discount: 20, image: 'https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=400&h=250&fit=crop' },
];

const busDeals = [
  { from: 'Johannesburg', to: 'Durban', price: 350, company: 'Greyhound', duration: '7h 30m', discount: 40, type: 'Luxury' },
  { from: 'Cape Town', to: 'Johannesburg', price: 499, company: 'Intercape', duration: '16h', discount: 35, type: 'Sleepliner' },
  { from: 'Pretoria', to: 'Bloemfontein', price: 280, company: 'City to City', duration: '5h', discount: 20, type: 'Standard' },
  { from: 'Durban', to: 'East London', price: 320, company: 'Translux', duration: '8h', discount: 25, type: 'Semi-Luxury' },
];

const accommodationDeals = [
  { name: 'Table Mountain Lodge', location: 'Cape Town', price: 1299, rating: 4.8, discount: 35, type: 'Hotel', image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=250&fit=crop' },
  { name: 'Sandton City Suites', location: 'Johannesburg', price: 1899, rating: 4.6, discount: 25, type: 'Apartment', image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=250&fit=crop' },
  { name: 'Umhlanga Beach Resort', location: 'Durban', price: 1599, rating: 4.9, discount: 40, type: 'Resort', image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400&h=250&fit=crop' },
  { name: 'Drakensberg Mountain Lodge', location: 'KwaZulu-Natal', price: 999, rating: 4.7, discount: 30, type: 'Lodge', image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=250&fit=crop' },
];

const SearchForm = ({ type }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [date, setDate] = useState('');
  const [passengers, setPassengers] = useState('1');

  const handleSearch = () => {
    if (!from || !to) { toast.error('Please enter departure and destination'); return; }
    toast.success(`Searching ${type}... Results will appear shortly`);
  };

  return (
    <Card className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm mb-6">
      <div className="grid md:grid-cols-5 gap-4">
        <div>
          <Label className="text-gray-600 text-xs">From</Label>
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="Departure" className="pl-9 bg-white border-gray-200 text-gray-900" data-testid={`${type}-from-input`} />
          </div>
        </div>
        <div>
          <Label className="text-gray-600 text-xs">To</Label>
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Destination" className="pl-9 bg-white border-gray-200 text-gray-900" data-testid={`${type}-to-input`} />
          </div>
        </div>
        <div>
          <Label className="text-gray-600 text-xs">Date</Label>
          <div className="relative mt-1">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="pl-9 bg-white border-gray-200 text-gray-900" />
          </div>
        </div>
        <div>
          <Label className="text-gray-600 text-xs">Passengers</Label>
          <Select value={passengers} onValueChange={setPassengers}>
            <SelectTrigger className="mt-1 bg-white border-gray-200 text-gray-900"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {[1,2,3,4,5,6].map(n => <SelectItem key={n} value={String(n)}>{n} {n===1?'Passenger':'Passengers'}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={handleSearch} className="w-full bg-afriperx-green hover:bg-afriperx-green-dark text-white" data-testid={`${type}-search-btn`}>
            <Search className="w-4 h-4 mr-2" /> Search
          </Button>
        </div>
      </div>
    </Card>
  );
};

export const TravelSection = () => {
  return (
    <div className="space-y-6" data-testid="travel-section">
      <div>
        <h1 className="font-syne text-2xl font-bold text-gray-900">Travel</h1>
        <p className="text-gray-500">Book flights, buses, and accommodation with exclusive AfriPerx discounts</p>
      </div>

      {/* Special Banner */}
      <Card className="bg-gradient-to-r from-afriperx-green to-afriperx-green-dark p-6 rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm font-medium">AFRIPERX EXCLUSIVE</p>
            <h2 className="font-syne text-2xl font-bold mt-1">Up to 40% Off Travel Deals</h2>
            <p className="text-white/80 mt-1">Use your loyalty points for extra savings</p>
          </div>
          <Tag className="w-12 h-12 text-white/40" />
        </div>
      </Card>

      <Tabs defaultValue="flights" className="w-full">
        <TabsList className="w-full grid grid-cols-3 bg-gray-100 rounded-xl p-1 h-auto">
          <TabsTrigger value="flights" className="data-[state=active]:bg-afriperx-green data-[state=active]:text-white rounded-lg py-3 text-gray-600 font-medium" data-testid="travel-flights-tab">
            <Plane className="w-4 h-4 mr-2" /> Flights
          </TabsTrigger>
          <TabsTrigger value="buses" className="data-[state=active]:bg-afriperx-green data-[state=active]:text-white rounded-lg py-3 text-gray-600 font-medium" data-testid="travel-buses-tab">
            <Bus className="w-4 h-4 mr-2" /> Buses
          </TabsTrigger>
          <TabsTrigger value="accommodation" className="data-[state=active]:bg-afriperx-green data-[state=active]:text-white rounded-lg py-3 text-gray-600 font-medium" data-testid="travel-accommodation-tab">
            <Hotel className="w-4 h-4 mr-2" /> Accommodation
          </TabsTrigger>
        </TabsList>

        {/* FLIGHTS */}
        <TabsContent value="flights" className="mt-6">
          <SearchForm type="flight" />
          <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">Flight Specials</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {flightDeals.map((deal, idx) => (
              <Card key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="h-36 bg-gray-200 relative overflow-hidden">
                  <img src={deal.image} alt={`${deal.from} to ${deal.to}`} className="w-full h-full object-cover" />
                  <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">{deal.discount}% OFF</div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 text-gray-900 font-bold">
                    <span>{deal.from}</span>
                    <Plane className="w-4 h-4 text-afriperx-gold" />
                    <span>{deal.to}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>{deal.airline}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{deal.duration}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <span className="text-gray-400 text-xs line-through">R{Math.round(deal.price / (1 - deal.discount/100))}</span>
                      <span className="text-afriperx-gold font-bold text-xl ml-2">R{deal.price}</span>
                    </div>
                    <Button size="sm" className="bg-afriperx-green hover:bg-afriperx-green-dark text-white" onClick={() => toast.success('Booking initiated! Redirecting...')}>
                      Book <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* BUSES */}
        <TabsContent value="buses" className="mt-6">
          <SearchForm type="bus" />
          <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">Bus Specials</h2>
          <div className="space-y-3">
            {busDeals.map((deal, idx) => (
              <Card key={idx} className="bg-white border border-gray-200 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Bus className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold">{deal.from} &rarr; {deal.to}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span>{deal.company}</span>
                        <span>{deal.type}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{deal.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">{deal.discount}% OFF</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-gray-400 text-xs line-through">R{Math.round(deal.price / (1 - deal.discount/100))}</span>
                      <span className="text-afriperx-gold font-bold text-lg ml-2">R{deal.price}</span>
                    </div>
                    <Button size="sm" className="mt-2 bg-afriperx-green hover:bg-afriperx-green-dark text-white" onClick={() => toast.success('Bus booking initiated!')}>
                      Book Now
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ACCOMMODATION */}
        <TabsContent value="accommodation" className="mt-6">
          <Card className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm mb-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-600 text-xs">Destination</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Where to?" className="pl-9 bg-white border-gray-200 text-gray-900" data-testid="accommodation-dest-input" />
                </div>
              </div>
              <div>
                <Label className="text-gray-600 text-xs">Check-in</Label>
                <Input type="date" className="mt-1 bg-white border-gray-200 text-gray-900" />
              </div>
              <div>
                <Label className="text-gray-600 text-xs">Check-out</Label>
                <Input type="date" className="mt-1 bg-white border-gray-200 text-gray-900" />
              </div>
              <div className="flex items-end">
                <Button className="w-full bg-afriperx-green hover:bg-afriperx-green-dark text-white" data-testid="accommodation-search-btn" onClick={() => toast.success('Searching accommodation...')}>
                  <Search className="w-4 h-4 mr-2" /> Search
                </Button>
              </div>
            </div>
          </Card>
          <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">Top Deals</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {accommodationDeals.map((deal, idx) => (
              <Card key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="h-40 bg-gray-200 relative overflow-hidden">
                  <img src={deal.image} alt={deal.name} className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 bg-white/90 text-gray-900 text-xs font-medium px-2 py-1 rounded-full">{deal.type}</div>
                  <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">{deal.discount}% OFF</div>
                </div>
                <div className="p-4">
                  <h3 className="text-gray-900 font-bold">{deal.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="w-3 h-3 text-gray-400" />
                    <span className="text-gray-500 text-sm">{deal.location}</span>
                    <div className="flex items-center gap-1 ml-auto">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      <span className="text-gray-700 text-sm font-medium">{deal.rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <span className="text-gray-400 text-xs line-through">R{Math.round(deal.price / (1 - deal.discount/100))}</span>
                      <span className="text-afriperx-gold font-bold text-xl ml-2">R{deal.price}</span>
                      <span className="text-gray-500 text-xs">/night</span>
                    </div>
                    <Button size="sm" className="bg-afriperx-green hover:bg-afriperx-green-dark text-white" onClick={() => toast.success('Booking initiated!')}>
                      Book <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
